import { Injectable } from "@angular/core";
import { BehaviorSubject, forkJoin, Observable } from "rxjs";
import { Subject } from "rxjs";
import { ErrorStateMatcher } from "@angular/material/core";
import {
  UntypedFormControl,
  FormGroupDirective,
  NgForm,
  ValidatorFn,
  FormControl,
  FormArray,
} from "@angular/forms";
import { NavigationExtras, Router } from "@angular/router";
import { MatDialog } from "@angular/material/dialog";
import { PermissionService } from "./permisson.service";
import { ActionPreviewComponent } from "src/app/shared/components/action-preview/action-preview.component";
import { AbstractControl, ValidationErrors } from "@angular/forms";
import { InformationBoxComponent } from "src/app/shared/information-box/information-box.component";
import { EntityBulkEditComponent } from "src/app/shared/components/entity-bulk-edit/entity-bulk-edit.component";
import {
  UserManagementType,
  PermissionLevel,
  BULK_ACTION_MAX_LIMIT,
} from "src/app/models/common";
import { UniversalConst } from "src/app/models/common";
import { BaseEntityService } from "./rest-common.service";
import { HttpClient } from "@angular/common/http";
import { map } from "rxjs/operators";
import { EntityDashboardEditComponent } from "src/app/shared/components/entity-dashboard-edit/entity-dashboard-edit.component";
import { TdspStaticService } from "./tdsp/tdsp-static.service";
import { GeoService } from "./geo.service";
import { UserService } from "./user.service";
import { redirectToAgnAdvCreate } from "src/app/models/user";

@Injectable()
export class CommonService extends BaseEntityService {
  bulkActionLimit: Number = BULK_ACTION_MAX_LIMIT;

  constructor(
    private router: Router,
    public dialog: MatDialog,
    private permissionService: PermissionService,
    public http: HttpClient
  ) {
    super();
  }
  private geoSubject = new Subject<any>();
  private splitGeoSubject = new Subject<any>();
  private daypartSubject = new Subject<any>();
  private dealSubject = new Subject<any>();
  private ivtData = new Subject<any>();
  private systemSubject = new Subject<any>();
  private splitSystemSubject = new Subject<any>();
  private showSideMenuOptions: Subject<any> = new Subject();
  private selectedQueryFromUser = new Subject<any>();
  private selectedBulkEditFields = new BehaviorSubject<any>(null);

  // Observable string sources
  private stepChangeFromSideMenu = new Subject<string>();
  private changeSidenavFromComponent = new Subject<any>();
  private setContentFromComponent = new Subject<any>();
  private setContentFromSidenav = new Subject<any>();
  private setSecondrySubMenuStatus = new Subject<any>();

  private reportPausePlay = new Subject<boolean>();

  // Observable string streams
  changeViewFromSideNav$ = this.stepChangeFromSideMenu.asObservable();
  changeViewFromComponent$ = this.changeSidenavFromComponent.asObservable();
  changeContentFromComponent = this.setContentFromComponent.asObservable();

  changeContentFromSidenav = this.setContentFromSidenav.asObservable();
  changeSecondarySubMenu = this.setSecondrySubMenuStatus.asObservable();

  reportPausePlayStatus = this.reportPausePlay.asObservable();

  getGeograhyData = this.geoSubject.asObservable();
  getSplitGeograhyData = this.splitGeoSubject.asObservable();
  getDaypart = this.daypartSubject.asObservable();
  getDealData = this.dealSubject.asObservable();
  getIvtData = this.ivtData.asObservable();
  getSystemData = this.systemSubject.asObservable();
  getSplitSystemData = this.splitSystemSubject.asObservable();
  getSelectedQueryFromUser = this.selectedQueryFromUser.asObservable();
  getSelectedBulkEditFields = this.selectedBulkEditFields.asObservable();

  permission_levels = PermissionLevel;

  // Service message commands
  changeReportStatus(status: boolean) {
    this.reportPausePlay.next(status);
  }

  changeStepFromSideMenu(step: string) {
    this.stepChangeFromSideMenu.next(step);
  }

  changeStepFromComponent(value: any) {
    this.changeSidenavFromComponent.next(value);
  }

  contentFromSideMenu(content: any) {
    this.setContentFromSidenav.next(content);
  }

  contentFromComponent(content: any) {
    this.setContentFromComponent.next(content);
  }

  setSecondorySubMenu(content: any) {
    this.setSecondrySubMenuStatus.next(content);
  }

  public getMenuFlag(): Observable<any> {
    return this.showSideMenuOptions.asObservable();
  }

  public setMenuFlag(
    menu:
      | "main_menu"
      | "advertiser_menu"
      | "platform_menu"
      | "option_menu"
      | "cdp_menu",
    optionGroup?: string,
    optionSubGroup: string = "",
    platformType: string = "",
    entityGroup: string = null
  ): void {
    this.showSideMenuOptions.next({
      menu: menu,
      optionGroup: optionGroup,
      optionSubGroup: optionSubGroup,
      platformType: platformType,
      entityGroup: entityGroup,
    });
  }

  setGeographyData(event) {
    this.geoSubject.next(event);
  }

  setSplitGeographyData(event) {
    this.splitGeoSubject.next(event);
  }

  setDaypart(event) {
    this.daypartSubject.next(event);
  }

  setSystemData(event) {
    this.systemSubject.next(event);
  }

  setDealData(event) {
    this.dealSubject.next(event);
  }

  setIvtData(event) {
    this.ivtData.next(event);
  }

  setSplitSystemData(event) {
    this.splitSystemSubject.next(event);
  }

  setSelectedQueryFromUser(event) {
    this.selectedQueryFromUser.next(event);
  }

  public setSelectedBulkEditFields(event) {
    /* Behvaioursubject sets to initial value,
     * if app or page reloads.Hence, setting it in local storage */
    localStorage.setItem("selectedFields", JSON.stringify(event));
    this.selectedBulkEditFields.next(event);
  }

  resetFilters(
    level:
      | "platform_filters"
      | "agency_filters"
      | "advertiser_filters"
      | "agency_advertiser_filters"
  ) {
    localStorage.setItem(level, JSON.stringify({}));
  }

  setFilters(
    component,
    filter,
    level:
      | "platform_filters"
      | "agency_filters"
      | "advertiser_filters"
      | "agency_advertiser_filters"
  ) {
    let filters = JSON.parse(localStorage.getItem(level)) || {};
    filters[component] = filter;
    localStorage.setItem(level, JSON.stringify(filters));
  }

  setSort(
    component,
    filter,
    level:
      | "platform_sorts"
      | "agency_sorts"
      | "advertiser_sorts"
      | "agency_advertiser_sorts"
  ) {
    let sort = JSON.parse(localStorage.getItem(level)) || {};
    sort[component] = filter;
    localStorage.setItem(level, JSON.stringify(sort));
  }

  getFilters(
    component,
    level:
      | "platform_filters"
      | "agency_filters"
      | "advertiser_filters"
      | "agency_advertiser_filters"
  ) {
    let filters = JSON.parse(localStorage.getItem(level));
    return filters ? filters[component] || {} : {};
  }

  getSorts(
    component,
    level:
      | "platform_sorts"
      | "agency_sorts"
      | "advertiser_sorts"
      | "agency_advertiser_sorts"
  ) {
    let sorts = JSON.parse(localStorage.getItem(level));
    return sorts ? sorts[component] || {} : {};
  }

  setDirtyStatus(status: boolean) {
    localStorage.setItem("isDirty", JSON.stringify(status));
  }

  getDirtyStatus() {
    return JSON.parse(localStorage.getItem("isDirty"));
  }

  // get events$ () {
  //   return this.geoSubject.asObservable();
  // }

  // public getAdvertiser(): Observable<any> {
  //   return this.advertiser.asObservable();
  // }

  // public setAdvertiser(value: number): void {
  //   this.advertiser.next(value);
  // }

  // public getAgency(): Observable<any> {
  //   return this.agency.asObservable();
  // }

  // public setAgency(value: number): void {
  //   this.agency.next(value);
  // }

  /**
   * @description Method to update side menu status during entity create/update
   */

  sideMenuStatus(side_bar_status, component: string) {
    this.setSecondorySubMenu({
      component: component,
      sideBarStatus: side_bar_status,
    });
  }

  /**
   * @description Method to update side menu step change during entity create/update
   * @return {string}
   */

  sideNavStepChange(step: string, component: string, componentName: string) {
    this.changeStepFromComponent({
      selectedTab: step,
      showSecondaryMenu: false,
      component: component,
      componentName: componentName,
    });
    return step;
  }

  /* Landing Page Utility function starts here*/

  getFiltersFromStorage(
    type,
    filterName,
    classObj,
    entity: string,
    isCampaignGrid: boolean = false,
    dsp: string
  ) {
    classObj.filterJSON = this.getFilters(filterName, type);
    classObj.selectedState = classObj.filterJSON["state"] || "all";

    if (entity == "agency_advertiser") {
      classObj.selectedVertical =
        classObj.filterJSON["selectedVertical"] || "all";
    }

    if (entity == "li" || entity == "crtv") {
      classObj.selectedAdType =
        classObj.filterJSON["selectedAdType"] ??
        (dsp === "bdsp" && entity === "crtv" ? "banner" : "all");
    }

    if (entity == "crtv" && dsp == "bdsp") {
      classObj.selectedNativeType =
        classObj.filterJSON["selectedNativeType"] || "all";
    }

    if ((entity == "io" && dsp == "apnx") || dsp == "bdsp") {
      classObj.selectedIoType = classObj.filterJSON["selectedIoType"] || "all";
      classObj.selectedIoDealType =
        classObj.filterJSON["selectedIoDealType"] || "all";
    } else if (entity == "li") {
      if (!classObj.selectedInsertion) {
        classObj.selectedInsertion =
          classObj.filterJSON["selectedInsertion"] || 0;
      }
    } else if (entity == "crtv") {
      if (dsp == "apnx") {
        classObj.selectedPlatformAuditStatus =
          classObj.filterJSON["selectedPlatformAuditStatus"] || "all";
        classObj.selectedAdxAuditStatus =
          classObj.filterJSON["selectedAdxAuditStatus"] || "all";
        classObj.selectedSize = classObj.filterJSON["selectedSize"] || "all";
      }
      if (dsp == "tdsp") {
        classObj.selectedXandrAuditStatus =
          classObj.filterJSON["selectedXandrAuditStatus"] || "all";
        classObj.selectedAdxAuditStatus =
          classObj.filterJSON["selectedAdxAuditStatus"] || "all";
        classObj.selectedSize = classObj.filterJSON["selectedSize"] || "all";
      }
      if (!classObj.selectedLineItem) {
        classObj.selectedLineItem =
          classObj.filterJSON["selectedLineItem"] || 0;
      }
      classObj.selectedStatus = classObj.filterJSON["selectedStatus"] || "all";
    }

    if (isCampaignGrid) {
      classObj.selectedDateRange =
        classObj.filterJSON["selectedDateRange"] ||
        UniversalConst.DEFAULT_STATS_TIME_PERIOD;
      if (dsp == "apnx") {
        classObj.selectedStartDate =
          classObj.filterJSON["selectedStartDate"] || null;
        classObj.selectedEndDate =
          classObj.filterJSON["selectedEndDate"] || null;
      }
    }
  }

  setFilterJSONValues(type, filterName, classObj) {
    classObj.filterJSON = {};

    if (classObj.selectedState && classObj.selectedState !== "all") {
      classObj.filterJSON["state"] = classObj.selectedState;
    }

    if (classObj.selectedStatus && classObj.selectedStatus !== "all") {
      classObj.filterJSON["selectedStatus"] = classObj.selectedStatus;
    }

    if (classObj.selectedVertical && classObj.selectedVertical !== "all") {
      classObj.filterJSON["selectedVertical"] = classObj.selectedVertical;
    }

    if (classObj.selectedNativeType) {
      classObj.filterJSON["selectedNativeType"] = classObj.selectedNativeType;
    }

    if (classObj.selectedIoType) {
      classObj.filterJSON["selectedIoType"] = classObj.selectedIoType;
    }

    if (classObj.selectedIoDealType) {
      classObj.filterJSON["selectedIoDealType"] = classObj.selectedIoDealType;
    }

    if (classObj.selectedAdType) {
      classObj.filterJSON["selectedAdType"] = classObj.selectedAdType;
    }

    if (classObj.selectedDateRange) {
      classObj.filterJSON["selectedDateRange"] = classObj.selectedDateRange;
    }

    if (classObj.selectedStartDate) {
      classObj.filterJSON["selectedStartDate"] = classObj.selectedStartDate;
    }

    if (classObj.selectedEndDate) {
      classObj.filterJSON["selectedEndDate"] = classObj.selectedEndDate;
    } else if (!classObj.selectedEndDate) {
      classObj.filterJSON["selectedEndDate"] = classObj.selectedStartDate;
    }

    if (classObj.selectedInsertion) {
      classObj.filterJSON["selectedInsertion"] = classObj.selectedInsertion;
    }

    if (classObj.selectedPlatformAuditStatus) {
      classObj.filterJSON["selectedPlatformAuditStatus"] =
        classObj.selectedPlatformAuditStatus;
    }

    if (classObj.selectedXandrAuditStatus) {
      classObj.filterJSON["selectedXandrAuditStatus"] =
        classObj.selectedXandrAuditStatus;
    }

    if (classObj.selectedAdxAuditStatus) {
      classObj.filterJSON["selectedAdxAuditStatus"] =
        classObj.selectedAdxAuditStatus;
    }

    if (classObj.selectedStatsTimeRange) {
      classObj.filterJSON["selectedStatsTimeRange"] =
        classObj.selectedStatsTimeRange;
    }

    if (classObj.selectedSize) {
      classObj.filterJSON["selectedSize"] = classObj.selectedSize;
    }

    if (classObj.selectedLineItem) {
      classObj.filterJSON["selectedLineItem"] = classObj.selectedLineItem;
    }

    this.setFilters(filterName, classObj.filterJSON, type);
  }

  getSortingFromStorage(type, sortName, classObj, entity: string, dsp: string) {
    classObj.sortJSON = this.getSorts(sortName, type);
    classObj.selectedState = classObj.filterJSON["state"] || "all";
    classObj.selectedColumn =
      classObj.sortJSON["selectedColumn"] || "modified_at";
    classObj.selectedSortBy =
      classObj.sortJSON["selectedSortBy"] || "ascending";
  }

  setSortingJSONValues(type, sortName, classObj) {
    classObj.sortJSON = {};

    if (classObj.selectedColumn && classObj.selectedColumn !== "all") {
      classObj.sortJSON["selectedColumn"] = classObj.selectedColumn;
    }

    if (classObj.selectedSortBy && classObj.selectedSortBy !== "all") {
      classObj.sortJSON["selectedSortBy"] = classObj.selectedSortBy;
    }
    this.setSort(sortName, classObj.sortJSON, type);
  }

  /* Landing Page Utility function end here*/

  getObjfromParams(paramMap) {
    let paramObj;
    paramMap.keys.map((key) => {
      paramObj = { ...paramObj, [key]: paramMap.get(key) };
    });
    return paramObj ?? "";
  }

  getclientID(obj) {
    let keys = ["advertiser_id", "agency_id", "agency_advertiser_id"];
    for (const key of keys) {
      if (obj.hasOwnProperty(key)) {
        return { [key]: obj[key] };
      }
    }
    return {};
  }

  setIdsFromParams(queryParams, classObj) {
    classObj.advertiserId = queryParams.get("advertiser_id");
    classObj.agcAdvId = queryParams.get("agency_advertiser_id");
    classObj.agencyId = queryParams.get("agency_id");
    classObj.adspRegion = queryParams.get("adsp_region");
    classObj.ruleCategory = queryParams.get("rule_category");
    classObj.insertionOrderId = parseInt(
      queryParams.get("bdsp_insertion_order_id") ||
        queryParams.get("adsp_insertion_order_id") ||
        queryParams.get("insertion_order_id") ||
        queryParams.get("tdsp_insertion_order_id")
    );
    classObj.lineItemId = parseInt(
      queryParams.get("bdsp_line_item_id") ||
        queryParams.get("adsp_line_item_id") ||
        queryParams.get("line_item_id") ||
        queryParams.get("tdsp_line_item_id")
    );
  }

  displayAvlCountry(countries) {
    let dialogRef = this.dialog.open(InformationBoxComponent, {
      width: "38%",
      disableClose: true,
      autoFocus: false,
      panelClass: 'information-modalbox',
      data: { type: 'gamblingCtry', countries: countries },
    });
  }

  goToLandingPage(route, params = {}) {
    let dialogRef = this.dialog.open(ActionPreviewComponent, {
      width: "45em",
      disableClose: true,
      panelClass: "actionpreview-modalbox",
      data: {
        action:
          "Unsaved changes will be lost. Please click OK to proceed without saving or CANCEL to continue editing.",
      },
    });
    dialogRef.afterClosed().subscribe((result) => {
      if (result.save) {
        this.router.navigate([route], { queryParams: params });
      }
    });
  }

  showValidMacros(validMacros) {
    let dialogRef = this.dialog.open(InformationBoxComponent, {
      width: "30%",
      disableClose: true,
      autoFocus: false,
      panelClass: "information-modalbox",
      data: { validMacros: validMacros, type: "validMacros" },
    });
  }

  showStateModal(
    type,
    client,
    client_type,
    admin_state,
    account_state,
    advertiser_state = "active"
  ) {
    let dialogRef = this.dialog.open(InformationBoxComponent, {
      width: "39em",
      // height: "15em",
      autoFocus: false,
      disableClose: true,
      panelClass: "information-modalbox",
      data: {
        type: type,
        client: client,
        clientType: client_type,
        adminState: admin_state,
        accountState: account_state,
        advertiserState: advertiser_state,
      },
    });
  }

  previousStepDirty(route, params = {}) {
    let dialogRef = this.dialog.open(ActionPreviewComponent, {
      width: "45em",
      height: "16em",
      disableClose: true,
      panelClass: "actionpreview-modalbox",
      data: {
        action:
          "Some unsaved changes in previous screens will be discarded. To avoid this please click 'save and continue' in the previous screens. Press Yes to discard changes and continue",
      },
    });
    dialogRef.afterClosed().subscribe((result) => {
      if (result.save) {
        this.router.navigate([route], { queryParams: params });
      }
    });
  }

  validateState(client, path, userRole, global_params, agency_advertiser_id) {
    if (
      client["admin_state"] == "active" &&
      userRole == "platform_superadmin" &&
      path != ""
    ) {
      this.router.navigate([path], { queryParams: global_params });
    } else if (
      client["admin_state"] == "active" &&
      userRole == "platform_superadmin" &&
      path == ""
    ) {
      return true;
    } else if (agency_advertiser_id && client["advertiser_state"] != "active") {
      this.showStateModal(
        "clientContent",
        client["client_name"],
        client["management_type"],
        client["admin_state"],
        client["account_state"],
        client["advertiser_state"]
      );
    } else if (
      client["admin_state"] == "active" &&
      client["account_state"] == "active" &&
      client["management_type"] == UserManagementType[userRole] &&
      path != ""
    ) {
      this.router.navigate([path], { queryParams: global_params });
    } else if (
      client["admin_state"] == "active" &&
      client["account_state"] == "active" &&
      client["management_type"] == UserManagementType[userRole] &&
      path == ""
    ) {
      return true;
    } else {
      this.showStateModal(
        "clientContent",
        client["client_name"],
        client["management_type"],
        client["admin_state"],
        client["account_state"]
      );
    }
  }

  /**
   * @description Checks whether client admin state/ account state is active to create
   * any new entity
   */

  createNew(
    globalParams = {},
    userRole: string,
    path: string,
    advertiserId,
    agencyId,
    agcAdvId,
    isAdsp = false,
    adspRegion = null,
    ruleType = null
  ) {
    let client_params = {},
      adspClientActivationStatus,
      client = {};
    client["client_name"] = advertiserId
      ? "Advertiser"
      : agencyId
      ? "Agency"
      : "agency_advertiser";
    client_params["name"] = agencyId
      ? "agency"
      : advertiserId
      ? "advertiser"
      : "agency-advertiser";
    client_params["id"] = agencyId
      ? agencyId
      : advertiserId
      ? advertiserId
      : agcAdvId;
    this.permissionService
      .getConfig(client_params["id"], client_params["name"])
      .subscribe((res) => {
        client["account_state"] = res.data.account_state;
        client["admin_state"] = res.data.admin_state;
        client["management_type"] = res.data.management_type;
        if (isAdsp) {
          adspClientActivationStatus = res.data.adsp_activated;
          globalParams["adsp_region"] = adspRegion;
        }
        if (agcAdvId) {
          client["advertiser_state"] = res.data.advertiser_state;
        }
        if (ruleType) {
          globalParams["rule_category"] = ruleType;
        }
        if (!isAdsp || agencyId) {
          this.validateState(client, path, userRole, globalParams, agcAdvId);
        } else if (adspClientActivationStatus && isAdsp) {
          this.validateState(client, path, userRole, globalParams, agcAdvId);
        } else {
          let dialogRef = this.dialog.open(InformationBoxComponent, {
            width: "39em",
            autoFocus: false,
            disableClose: true,
            panelClass: "information-modalbox",
            data: { type: "adspModel" },
          });
        }
      });
  }

  validPassword(value) {
    var inValid: Boolean = false;
    const specialRegexp: RegExp = /[!@#$%^&*()_+\-=\[\]{};:\\|,.<>\/?]/;
    const upperRegexp: RegExp = /[A-Z]/;
    const numberRegexp: RegExp = /[0-9]/;
    const excemptionRegexp: RegExp = /['"]/;
    if (value && !specialRegexp.test(value)) {
      inValid = true;
    }
    if (value && !upperRegexp.test(value)) {
      inValid = true;
    }
    if (value && !numberRegexp.test(value)) {
      inValid = true;
    }
    if (value && excemptionRegexp.test(value)) {
      inValid = true;
    }
    return inValid;
  }

  validEmail(value) {
    var inValid: Boolean = false;
    var format: RegExp =
      /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)*$/;
    if (value && !format.test(value)) {
      inValid = true;
    }
    return inValid;
  }

  passwordValidation() {
    return (control: AbstractControl): ValidationErrors | null => {
      return this.validPassword(control.value) ? { formatError: true } : null;
    };
  }

  goToExternalDomain(url) {
    window.location.href = url;
  }

  isStartDateOverlappingFormArray(intervalForm: FormArray) {
    return (control: AbstractControl): ValidationErrors | null => {
      if (intervalForm.length <= 1) return null;

      const start_date = control.value;
      const isOverlapping = intervalForm.controls.some(
        (match_interval) =>
          match_interval.enabled &&
          start_date <= match_interval.get("end_date")?.value &&
          start_date > match_interval.get("start_date")?.value
      );

      return isOverlapping ? { overlapError: true } : null;
    };
  }

  isEndDateOverlappingFormArray(intervalForm: FormArray) {
    return (control: AbstractControl): ValidationErrors | null => {
      if (intervalForm.length <= 1) return null;

      const end_date = control.value;
      const isOverlapping = intervalForm.controls.some(
        (match_interval) =>
          match_interval.enabled &&
          end_date >= match_interval.get("start_date")?.value &&
          end_date < match_interval.get("end_date")?.value
      );

      return isOverlapping ? { overlapError: true } : null;
    };
  }

  isOverallDateRangeOverlapFormArray(intervalForm: FormArray) {
    return (control: AbstractControl): ValidationErrors | null => {
      let isOverlapping = false;

      intervalForm.controls.forEach((interval, i) => {
        if (!interval.enabled) return;
        const start_date = interval.get("start_date")?.value;
        const end_date = interval.get("end_date")?.value;

        intervalForm.controls.forEach((match_interval, j) => {
          if (i === j || !match_interval.enabled) return;

          const match_start = match_interval.get("start_date")?.value;
          const match_end = match_interval.get("end_date")?.value;

          if (end_date >= match_end && start_date <= match_start) {
            isOverlapping = true;
          }
        });
      });

      return isOverlapping ? { overlapError: true } : null;
    };
  }

  isStartDateOverlapping(intervalForm) {
    // console.log('overlap check...')
    return (control: AbstractControl): ValidationErrors | null => {
      var isOverlapping: Boolean = false;
      if (intervalForm.length > 1) {
        var start_date = control.value;
        intervalForm.forEach((match_interval) => {
          if (
            start_date <= match_interval.controls.end_date.value &&
            start_date > match_interval.controls.start_date.value
          ) {
            isOverlapping = true;
          }
        });
      }
      // console.log('isOverlapping>>', isOverlapping)
      return isOverlapping ? { overlapError: true } : null;
    };
  }

  isEndDateOverlapping(intervalForm) {
    // console.log('overlap check...')
    return (control: AbstractControl): ValidationErrors | null => {
      var isOverlapping: Boolean = false;
      if (intervalForm.length > 1) {
        var end_date = control.value;
        intervalForm.forEach((match_interval) => {
          if (
            end_date >= match_interval.controls.start_date.value &&
            end_date < match_interval.controls.end_date.value
          ) {
            isOverlapping = true;
          }
        });
      }
      // console.log('isOverlapping>>', isOverlapping)
      return isOverlapping ? { overlapError: true } : null;
    };
  }

  isOverallDateRangeOverlap(intervalForm) {
    return (control: AbstractControl): ValidationErrors | null => {
      var isOverlapping: Boolean = false;
      if (intervalForm.length > 1) {
        intervalForm.forEach((interval, i) => {
          var start_date = interval.controls.start_date.value;
          var end_date = interval.controls.end_date.value;
          intervalForm.forEach((match_interval, j) => {
            if (
              i != j &&
              end_date >= match_interval.controls.end_date.value &&
              start_date <= match_interval.controls.start_date.value
            ) {
              isOverlapping = true;
            }
          });
        });
      }
      // console.log('isOverlapping>>', isOverlapping)
      return isOverlapping ? { overlapError: true } : null;
    };
  }

  isPastDate(clientTimeZone) {
    return (control: AbstractControl): ValidationErrors | null => {
      var isPastError: Boolean = false;
      var date = control.value;
      // let today = new Date();
      let today_client_tz = new Date().toLocaleString("en-US", {
        timeZone: clientTimeZone,
      });
      let today = new Date(today_client_tz);
      today.setDate(today.getDate() - 1);
      if (date < today) {
        isPastError = true;
      }
      // console.log('isPastError>>', isPastError)
      return isPastError ? { pastError: true } : null;
    };
  }

  isEndDateBeforeStartDate() {
    return (control: AbstractControl): ValidationErrors | null => {
      var isEndDateError: Boolean = false;
      var start_date = control.parent?.value.start_date;
      var date = control.value;
      if (date < start_date) {
        isEndDateError = true;
      }
      return isEndDateError ? { endDateBeforeStartDateError: true } : null;
    };
  }

  isValidInterval(
    start_date,
    end_date,
    clientTimeZone,
    isEdit,
    dsp = "none",
    blockedIntervalList = []
  ) {
    let interval_valid = true;
    let today_client_tz = new Date().toLocaleString("en-US", {
      timeZone: clientTimeZone,
    });
    let today = new Date(today_client_tz);
    today.setDate(today.getDate() - 1);
    if (!start_date) {
      interval_valid = false;
    }
    if (!end_date) {
      interval_valid = false;
    }
    if (start_date < today && !isEdit) {
      interval_valid = false;
    }
    if (end_date < start_date) {
      interval_valid = false;
    }
    if (dsp == "bdsp" && end_date) {
      end_date.setHours(23, 59, 59);
      if (end_date < today.setDate(today.getDate() + 1)) {
        interval_valid = false;
      }
    }
    if (blockedIntervalList.length != 0) {
      blockedIntervalList.forEach((intr) => {
        if (
          (start_date <= intr.end_date && start_date >= intr.start_date) ||
          (end_date <= intr.end_date && end_date >= intr.start_date)
        ) {
          interval_valid = false;
        }
      });
    }
    return interval_valid;
  }

  dateRangeOverlapping(date, index, budget_intervals) {
    let isOverlapping = false;
    budget_intervals.forEach((flight, i) => {
      if (index != i) {
        if (date <= flight.end_date && date >= flight.start_date) {
          isOverlapping = true;
        }
      }
    });
    return isOverlapping;
  }

  validateIoDate(date) {
    let today = new Date();
    if (today > new Date(date)) {
      return true;
    }
    return false;
  }

  getDistinctArray(value) {
    return value.filter((item, index) => value.indexOf(item) === index);
  }

  getDistinctArrayOfObjects(value) {
    let unique_object = [];
    let map = new Map();
    value.forEach((obj) => {
      const key = JSON.stringify(obj);
      if (!map.has(key)) {
        map.set(key, true);
        unique_object.push(obj);
      }
    });

    return unique_object;
  }

  removeMissingSupplySources(sourceList, addedSources) {
    return sourceList.filter((obj) => {
      const comparisonSet = new Set(
        addedSources.map((item) => JSON.stringify(item.id))
      );
      return comparisonSet.has(JSON.stringify(obj.id));
    });
  }

  getAllCheckboxSelected(value, pageIndex, pageSize) {
    let allCheckboxSelected;
    let samePage;
    for (
      let i = pageIndex == 0 ? 0 : pageSize * pageIndex;
      i < pageSize * (pageIndex + 1) && i < value.length;
      i++
    ) {
      samePage = true;
      if (!value[i]["selected"]) {
        samePage = false;
        break;
      }
    }
    if (samePage == true) allCheckboxSelected = true;
    else allCheckboxSelected = false;
    return allCheckboxSelected;
  }

  isCheckboxIntermediate(value, pageSize, pageIndex, allCheckboxSelected) {
    let samePage;
    for (
      let i = pageIndex == 0 ? 0 : pageSize * pageIndex;
      i <
        (value.length < pageSize ? value.length : pageSize) * (pageIndex + 1) &&
      i < value.length;
      i++
    ) {
      samePage = false;
      if (value[i]["selected"] && !allCheckboxSelected) {
        samePage = true;
        break;
      }
    }
    if (samePage == true) return true;
    else return false;
  }

  selectAllCheckbox(value, pageIndex, pageSize, checked) {
    for (
      let i = pageIndex == 0 ? 0 : pageSize * pageIndex;
      i < pageSize * (pageIndex + 1) && i < value.length;
      i++
    ) {
      value[i].selected = checked;
    }
    return value;
  }

  getIOMinMaxDate(selectedInsertionData, clientTimeZone, type) {
    let today_client_tz = new Date().toLocaleString("en-US", {
      timeZone: clientTimeZone,
    });
    let minFlightDate, maxFlightDate, ioStartDate;
    let today = new Date(today_client_tz);
    minFlightDate = new Date(
      selectedInsertionData.budget_intervals[0].start_date
    );
    maxFlightDate = new Date(
      selectedInsertionData.budget_intervals[0].end_date
    );
    minFlightDate.setHours(0, 0, 0, 0);
    today.setHours(0, 0, 0, 0);
    selectedInsertionData.budget_intervals.forEach((ele) => {
      let end_date = new Date(ele.end_date);
      let start_date = new Date(ele.start_date);
      if (minFlightDate > start_date) {
        minFlightDate = start_date;
      }
      if (end_date > maxFlightDate) {
        maxFlightDate = end_date;
      }
    });
    ioStartDate = minFlightDate;
    if (type == "lineitem") {
      if (minFlightDate < today) {
        minFlightDate = today;
      }
    }
    return { ioStartDate, minFlightDate, maxFlightDate };
  }

  getIOCumulativeBudget(selectedInsertionData) {
    return selectedInsertionData.budget_intervals.reduce(
      (acc, interval) => acc + parseInt(interval.flight_budget),
      0
    );
  }

  convertDateToString(start_date, end_date, isEdit, type) {
    var startDate = new Date(start_date);
    var isoStartDateTime = new Date(
      startDate.getTime() - startDate.getTimezoneOffset() * 60000
    )
      .toISOString()
      .slice(0, 10);

    var endDate = new Date(end_date);
    var isoEndDateTime = new Date(
      endDate.getTime() - endDate.getTimezoneOffset() * 60000
    )
      .toISOString()
      .slice(0, 10);

    if (type == "getDateAndTime") {
      let startTime = `${startDate
        .getHours()
        .toString()
        .padStart(2, "0")}:${startDate
        .getMinutes()
        .toString()
        .padStart(2, "0")}:${startDate
        .getSeconds()
        .toString()
        .padStart(2, "0")}`;

      if (!isEdit) {
        start_date = `${isoStartDateTime} 00:00:00`;
        end_date = `${isoEndDateTime} 23:59:59`;
      } else {
        start_date = `${isoStartDateTime} ${startTime}`;
        end_date = `${isoEndDateTime} 23:59:59`;
      }
    } else {
      start_date = isoStartDateTime;
      end_date = isoEndDateTime;
    }
    return { start_date, end_date };
  }

  isDatePastDate(date, clientTimeZone) {
    let today_client_tz = new Date().toLocaleString("en-US", {
      timeZone: clientTimeZone,
    });
    let today = new Date(today_client_tz);
    today.setDate(today.getDate() - 1);
    date = new Date(JSON.parse(JSON.stringify(date)));
    if (date < today) {
      return true;
    } else {
      return false;
    }
  }

  isAvgDailyBudgetHigher(budgetInterval, clientTimeZone) {
    var avgExceeds: Array<any> = [];
    budgetInterval.forEach((interval) => {
      // if pacing is evenly & both dates are in past, ignore.
      //else proceed
      if (
        !interval.pace_evenly &&
        (!this.isDatePastDate(interval.start_date, clientTimeZone) ||
          !this.isDatePastDate(interval.start_date, clientTimeZone))
      ) {
        let startDate, endDate;
        startDate = new Date(JSON.parse(JSON.stringify(interval.start_date)));
        endDate = new Date(JSON.parse(JSON.stringify(interval.end_date)));

        const diffTime = Math.abs(startDate - endDate);
        const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24)) + 1;
        if (
          interval.revenue_budget <
          diffDays * interval.daily_revenue_budget
        ) {
          avgExceeds.push({
            ...interval,
            avg_daily_budget: interval.revenue_budget / diffDays,
          });
        }
      }
    });
    return avgExceeds;
  }

  checkBulkActionLimit(selectedItems) {
    return (
      selectedItems.filter((ele) => ele.selected).length <= this.bulkActionLimit
    );
  }

  redirectTo(path, globalParams, adspRegion) {
    let navigationExtras: NavigationExtras = {
      queryParams: globalParams,
      skipLocationChange: true,
    };
    this.router.navigate(["/"], navigationExtras).then(() =>
      this.router.navigate([path], {
        queryParams: { ...globalParams, adsp_region: adspRegion },
      })
    );
  }

  dashboardEditScreen(
    entityItems: Array<any>,
    selectedItems: Array<any>,
    entityType: string,
    areAllEntitySelected: Object,
    globalParams: Object,
    redirectPath: string,
    dspType: string,
    adspRegion: string
  ) {
    let dialog = this.dialog.open(EntityDashboardEditComponent, {
      width: "60%",
      autoFocus: false,
      disableClose: true,
      panelClass: "entity-dashboard-edit-modalbox",
      data: {
        selectedItems: selectedItems,
        entityType: entityType,
        globalParams: globalParams,
        dsp: dspType,
      },
    });

    dialog.afterClosed().subscribe((res) => {
      if (res.save) {
        this.redirectTo(redirectPath, globalParams, adspRegion);
      } else {
        entityItems.map((ele) => (ele.selected = false));
        areAllEntitySelected["areAllItemSelected"] = false;
      }
    });
  }

  /**
   * @description Method to show screen to pick fields for bulk -edit-screen
   * @params entityItems, selectedItems, entityType, fields available for bulk edit,
   * @params bulkedit redirection path, globalparams
   *
   */
  pickFieldsForBulkEdit(
    entityItems: Array<any>,
    selectedItems: Array<any>,
    entityType: string,
    bulkEditPath: string,
    areAllEntitySelected: Object,
    globalParams: Object,
    dspType: string = "bdsp",
    action: string = null
  ) {
    let dialog = this.dialog.open(EntityBulkEditComponent, {
      width: "60%",
      autoFocus: false,
      disableClose: true,
      panelClass: "entity-bulk-edit-modalbox",
      data: {
        selectedItems: selectedItems,
        type: entityType,
        globalParams: globalParams,
        dspType: dspType,
        action: action,
      },
    });

    dialog.afterClosed().subscribe((res) => {
      /* If user chooses fields for edit and clicks on save,
       * redirect to respective bulk-edit page
       * else close the modal and clear all the selection
       * in respective landing page */

      if (res.save) {
        this.router.navigate([bulkEditPath], {
          queryParams: { ...globalParams, ids: res.selectedItems },
        });
      } else {
        entityItems.map((ele) => (ele.selected = false));
        areAllEntitySelected["areAllItemSelected"] = false;
      }
    });
  }

  openDuplicateOptions(id, agencyId, source) {
    let dialogRef = this.dialog.open(EntityBulkEditComponent, {
      width: "45%",
      disableClose: true,
      autoFocus: false,
      panelClass: "entity-bulk-edit-modalbox",
      data: {
        type: "cloneAdv",
        source: source,
        agencyId: agencyId,
      },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result.save) {
        let adsp_region = null;
        if (result.selectedItems === "adsp_us") {
          adsp_region = "US";
        } else if (result.selectedItems === "adsp_eu") {
          adsp_region = "EU";
        }
        this.router.navigate([redirectToAgnAdvCreate[result.selectedItems]], {
          queryParams: {
            agency_id: agencyId,
            duplicate: id,
            adsp_region,
          },
        });
      }
    });
  }

  getAsOfDate(params = {}): Promise<string> {
    let url = this.buildUrl(`${this.BASE_SHARED_URL}/as-of/`, "", params);
    return this.get(url)
      .pipe(map((response) => response.data["as_of"]))
      .toPromise() as Promise<string>;
  }

  getListOfValidMacros(params = {}): Promise<Object> {
    let url = this.buildUrl(
      `${this.BASE_SHARED_URL}/valid-macros/`,
      "",
      params
    );
    return this.get(url)
      .pipe(map((response) => response.data))
      .toPromise() as Promise<Object>;
  }

  getAllroles(params = {}): Promise<Array<Object>> {
    let url = this.buildUrl(`${this.BASE_SHARED_URL}/role-types/`, "", params);
    return this.get(url)
      .pipe(map((response) => response.data))
      .toPromise() as Promise<Array<Object>>;
  }

  getUsersByRole(params = {}) {
    let url = this.buildUrl(`${this.BASE_SHARED_URL}/user/`, "", params);
    return this.get(url);
  }

  checkPermissionLevel(granted_permission: string, check_for: string) {
    return (
      this.permission_levels[granted_permission.toUpperCase()] >=
      this.permission_levels[check_for.toUpperCase()]
    );
  }

  nameOverlap(form, items, field) {
    return (control: AbstractControl): ValidationErrors | null => {
      var isOverlapping: Boolean = false;
      let name = control.value;
      let index;
      form.forEach((ele, i) => {
        if (ele.controls[field] == control) index = i;
      });
      items.forEach((item, i) => {
        if (item[field] == name && i != index) {
          isOverlapping = true;
        }
      });
      return isOverlapping ? { overlapError: true } : null;
    };
  }

  displayEntitySaveBtn(isEdit, isEditPermission, isBulkEdit = false): boolean {
    return isBulkEdit || !isEdit || isEditPermission;
  }

  isUrlValid(url) {
    if (!url) return false;
    var res = url.match(
      /^(https?:\/\/)([a-zA-Z0-9-]+\.)+[a-zA-Z]{2,}(\/[^*\s]*)?$/
    );
    if (res == null) return false;
    else return true;
  }

  containHTTP(url) {
    if (url) {
      if (url.includes("http://")) return true;
      else return false;
    }
  }

  secureUrl(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const isValidUrl = !this.isUrlValid(control.value);
      return isValidUrl ? { invalidURL: { value: control.value } } : null;
    };
  }

  checkHTTP(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const isHTTP = this.containHTTP(control.value);
      return isHTTP ? { isHTTP: { value: control.value } } : null;
    };
  }
}

export class PasswordErrorStateMatcher implements ErrorStateMatcher {
  isErrorState(
    control: UntypedFormControl | null,
    form: FormGroupDirective | NgForm | null
  ): boolean {
    if (
      form.form.controls.confirmPassword.errors == null &&
      form.form.controls.password.errors == null &&
      form.form.value.confirmPassword === form.form.value.password
    ) {
      return;
    }
    if (form.form.controls.password.value != undefined) {
      const invalidCtrl = !!(
        control &&
        control.invalid &&
        control.parent.dirty
      );
      const invalidParent = !!(
        control &&
        control.parent &&
        control.parent.invalid &&
        control.parent.dirty
      );
      return invalidCtrl || invalidParent;
    }
  }
}

export class CommissionStateMatcher implements ErrorStateMatcher {
  isErrorState(
    control: UntypedFormControl | null,
    form: FormGroupDirective | NgForm | null
  ): boolean {
    return control.status == "INVALID";
  }
}

export class ManualErrorStateMatcher implements ErrorStateMatcher {
  isErrorState(control: FormControl | null): boolean {
    return !!(control && control.invalid);
  }
}

@Injectable({
  providedIn: "root",
})
export class ClientHelperService extends BaseEntityService {
  constructor(
    private tdspStaticService: TdspStaticService,
    private geoService: GeoService,
    private userService: UserService,
    public http: HttpClient
  ) {
    super();
  }

getTdspIndustryList(
  advertiser,
  type,
  parentId = null,
  search = null,
  categoryType?,
  isAgcAdv = false
): Observable<any[]> {
  let params: any = {
    start_element: 0,
    num_elements: 300,
  };
  if(categoryType){
    params.category_type = categoryType;
  }

  if (parentId) {
    params.parent_id = parentId;
  }

  if(!parentId && isAgcAdv){
    params.agency_id = advertiser?.agency_id || advertiser?.agencyId
  }

  if (search) {
    params.search = search;
  }

  return this.tdspStaticService
      .getStaticData(type, 0, 300, params)
      .pipe(map((res) => res.data));
}

getAgencyAdvCategories(advertiser,type, parentId = null, search = null, isAgcAdv = false): Observable<any[]> {

  let params: any = {
    start_element: 0,
    num_elements: 300,
  };

  if(!parentId && isAgcAdv){
    params.agency_id = advertiser?.agency_id || advertiser?.agencyId;
  }

  if (parentId) {
    params.parent_id = parentId;
  }

  if (search) {
    params.search = search;
  }

  return this.tdspStaticService
    .getStaticData(type, 0, 300, params)
    .pipe(map((res) => res.data));
}
  getCountries(
    type: "country" | "region" | "city" | "dma",
    category: "billing" | "organization" | null = null,
    search: string | null = null,
    start_element: number = 0,
    num_elements: number = 246,
    extraParams: any = {}
  ): Observable<{ billing?: any[]; organization?: any[] }> {
    const params: any = { ...extraParams };

    if (search) {
      params["search_fields"] = "name";
      params["name"] = search;
    }

    return this.geoService
      .getGeo(type, start_element, num_elements, params)
      .pipe(
        map((res) => {
          const result: { billing?: any[]; organization?: any[] } = {};
          if (category === "billing" || !category)
            result.billing = res.countries;
          if (category === "organization" || !category)
            result.organization = res.countries;
          return result;
        })
      );
  }

  getStates(
    type: "country" | "region" | "city" | "dma",
    countryName: string,
    vertical: string = "",
    category: "billing_country" | "country",
    search: string | null = null,
    start_element: number = 0,
    num_elements: number = 300,
    extraParams: any = {}
  ): Observable<{ billingStates?: any[]; organizationStates?: any[] }> {
    const params: any = { ...extraParams };
    params["country_name"] = countryName;
    params["search_fields"] = "country_name";

    if (search) {
      params["search_fields"] = "name,country_name";
      params["name"] = search;
    }

    return this.geoService
      .getGeo(type, start_element, num_elements, params)
      .pipe(
        map((res) => {
          const result: { billingStates?: any[]; organizationStates?: any[] } =
            {};
          if (category === "billing_country" || vertical === "political") {
            result.billingStates = res.regions;
          }
          if (category === "country" || vertical === "political") {
            result.organizationStates = res.regions;
          }
          return result;
        })
      );
  }

  getCurrencyOptions(): Observable<any[]> {
    return this.userService
      .getCurrencyOptions()
      .pipe(map((res) => res.currencies || []));
  }

  getTimeZoneOptions(): Observable<any[]> {
    return this.userService
      .getTimeZones()
      .pipe(map((res) => res.timezones || []));
  }

  // Optional: if you want both together
  getCurrencyAndTimeZones(): Observable<{
    currencies: any[];
    timezones: any[];
  }> {
    return forkJoin({
      currencies: this.getCurrencyOptions(),
      timezones: this.getTimeZoneOptions(),
    });
  }

  setCurrencyBasedOnCtry(adspTargetOption, client) {
    client.currency = adspTargetOption.find(
      (country) => country.value == client.adsp_target_country
    )?.currency;
  }
}
